import { useCallback, useRef, useState } from 'react';
import type { MessageTemplate, SendTemplate } from '@chatapp-inbox/shared';
import { templateKey, valueProblems } from './form';

export interface TemplatePickerState {
  templates: readonly MessageTemplate[];
  /** O modelo escolhido (ou o único disponível); `null` = nada escolhido. */
  selected: MessageTemplate | null;
  selectedKey: string;
  values: Readonly<Record<string, string>>;
  /** Erro por variável (`key`), mostrado no campo. */
  errors: Readonly<Record<string, string>>;
  selectError: string | null;
  select: (key: string) => void;
  setValue: (key: string, value: string) => void;
  /** Liga o campo de uma variável (e a lista) ao foco de `validate`. */
  registerField: (key: string, element: HTMLElement | null) => void;
  /** Confere o que foi preenchido; com problema, mostra o erro e leva o foco ao primeiro campo. */
  validate: () => SendTemplate | null;
}

/**
 * O estado da escolha do modelo e dos valores das variáveis. Com um único modelo utilizável, ele já
 * vem escolhido. `onEdit` avisa que a pessoa mudou algo (o chamador renova o `clientId` do envio: um
 * reenvio idêntico é idempotente, um reenvio com outro conteúdo não pode reaproveitar o identificador).
 */
export function useTemplatePicker(
  templates: readonly MessageTemplate[],
  onEdit?: () => void,
): TemplatePickerState {
  const [chosenKey, setChosenKey] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectError, setSelectError] = useState<string | null>(null);
  const fields = useRef(new Map<string, HTMLElement | null>());

  const usable = templates.filter((t) => t.supported);
  const chosen = templates.find((t) => t.supported && templateKey(t) === chosenKey) ?? null;
  const selected = chosen ?? (usable.length === 1 ? usable[0]! : null);
  const selectedKey = selected ? templateKey(selected) : '';

  const select = useCallback(
    (key: string) => {
      setChosenKey(key);
      setValues({});
      setErrors({});
      setSelectError(null);
      onEdit?.();
    },
    [onEdit],
  );
  const setValue = useCallback(
    (key: string, value: string) => {
      setValues((current) => ({ ...current, [key]: value }));
      setErrors((current) => {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
      onEdit?.();
    },
    [onEdit],
  );
  const registerField = useCallback((key: string, element: HTMLElement | null) => {
    if (element) fields.current.set(key, element);
    else fields.current.delete(key);
  }, []);

  const validate = useCallback((): SendTemplate | null => {
    if (!selected) {
      setSelectError('Escolha um modelo de mensagem.');
      fields.current.get('select')?.focus();
      return null;
    }
    const problems = valueProblems(selected, values);
    setSelectError(null);
    setErrors(problems);
    const firstKey = selected.variables.find((v) => problems[v.key])?.key;
    if (firstKey !== undefined) {
      fields.current.get(firstKey)?.focus();
      return null;
    }
    return { name: selected.name, language: selected.language, values: { ...values } };
  }, [selected, values]);

  return {
    templates,
    selected,
    selectedKey,
    values,
    errors,
    selectError,
    select,
    setValue,
    registerField,
    validate,
  };
}
